import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { updatePageMetadata, pageMetadata } from '../utils/pageMetadata';
import ExploreGrid from './ExploreGrid';
import PlaceDetailPanel from './PlaceDetailPanel';
import MapViewLite, { MarkerData } from './MapViewLite';
import TravelFilterUI, { FilterState } from './TravelFilterUI';
import FilterButton from './FilterButton';
import RadiusDropdown from './RadiusDropdown';
import { exploreService } from '../services/exploreService';
import { ExploreType, PlaceItem, ExploreState, ExploreFilters } from '../types/explore';
import { useSavedPlaces } from '../contexts/SavedPlacesContext';
import { useBottomSheet } from '../hooks/useBottomSheet';
import { useToast } from '../contexts/ToastContext';
import { API_BASE_URL } from '../config';

const Explore: React.FC = () => {
  const { savedPlaces } = useSavedPlaces();
  const { showToast } = useToast();
  
  // Update page metadata on mount
  useEffect(() => {
    updatePageMetadata(pageMetadata.explore.title, pageMetadata.explore.description);
  }, []);
  
  const [isSearchingNearby, setIsSearchingNearby] = useState(false);
  const [nearbyLocation, setNearbyLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [nearbyMode, setNearbyMode] = useState(false);
  const [selectedRadius, setSelectedRadius] = useState<number>(50);
  const [showRadiusDropdown, setShowRadiusDropdown] = useState(false);
  
  // Watch for nearby mode changes and load places when nearby mode is activated
  useEffect(() => {
    if (nearbyMode && nearbyLocation) {
      const currentType = stateRef.current.currentType;
      loadPlacesRef.current?.(currentType, true);
    }
  }, [nearbyMode, nearbyLocation, selectedRadius]);
  
  const [state, setState] = useState<ExploreState>({
    currentType: 'attraction',
    places: [],
    loading: false,
    hasMore: true,
    lastDoc: undefined,
    error: undefined,
    cache: {
      curated: { places: [], lastDoc: undefined, hasMore: true },
      attraction: { places: [], lastDoc: undefined, hasMore: true },
      food: { places: [], lastDoc: undefined, hasMore: true },
      hotel: { places: [], lastDoc: undefined, hasMore: true },
      experience: { places: [], lastDoc: undefined, hasMore: true }
    }
  });

  const [selectedPlace, setSelectedPlace] = useState<PlaceItem | null>(null);
  const [mapMarkers, setMapMarkers] = useState<MarkerData[]>([]);
  const [highlightedPlaceId, setHighlightedPlaceId] = useState<string | null>(null);
  const [hoveredPlaceId, setHoveredPlaceId] = useState<string | null>(null); // Separate state for hover
  const [touchedPlaceId, setTouchedPlaceId] = useState<string | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | undefined>(undefined);
  const [mapZoom, setMapZoom] = useState<number | undefined>(undefined);

  // Ref for scrollable content to prevent pull-to-refresh
  const contentScrollRef = useRef<HTMLDivElement>(null);

  // Use bottom sheet hook for smooth swipe behavior
  const {
    sheetState,
    sheetHeight,
    isDragging,
    expand,
    dragHandlers,
  } = useBottomSheet('half', {
    collapsed: 10,   // Minimal peek with centered text
    half: 50,        // Half screen - balanced view
    full: 80,        // Moved down a bit - focus on list (account for bottom nav)
  });

  // Calculate map height from sheet height (inverse)
  const mobileMapHeight = 100 - sheetHeight;

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterState | null>(null);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  const pageSize = 12;

  // Use ref to store stable filter reference
  const activeFiltersRef = useRef<FilterState | null>(null);
  
  // Update ref when activeFilters changes
  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  // Use ref for state access to avoid stale closures
  const stateRef = useRef(state);
  
  // Keep state ref updated
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Use ref for synchronous loading guard
  const isLoadingRef = useRef(false);

  // Prevent pull-to-refresh when at top of scrollable content
  useEffect(() => {
    const contentEl = contentScrollRef.current;
    if (!contentEl) return;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      contentEl.dataset.touchStartY = touch.clientY.toString();
      contentEl.dataset.scrollTopStart = contentEl.scrollTop.toString();
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const startY = parseFloat(contentEl.dataset.touchStartY || '0');
      const deltaY = touch.clientY - startY;
      const scrollTop = contentEl.scrollTop;

      // If at top and pulling down, prevent pull-to-refresh
      if (scrollTop <= 0 && deltaY > 0) {
        e.preventDefault();
      }
    };

    contentEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    contentEl.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      contentEl.removeEventListener('touchstart', handleTouchStart);
      contentEl.removeEventListener('touchmove', handleTouchMove);
    };
  }, [sheetState]);

  // Use ref for loadPlaces function to avoid dependency issues
  const loadPlacesRef = useRef<(type: ExploreType, reset?: boolean) => Promise<void>>(async () => {});
  
  // Use refs to store current nearby mode state for use in loadPlaces
  const nearbyModeRef = useRef(nearbyMode);
  const nearbyLocationRef = useRef(nearbyLocation);
  const selectedRadiusRef = useRef(selectedRadius);
  
  // Update refs whenever state changes
  useEffect(() => {
    nearbyModeRef.current = nearbyMode;
    nearbyLocationRef.current = nearbyLocation;
    selectedRadiusRef.current = selectedRadius;
  }, [nearbyMode, nearbyLocation, selectedRadius]);

  // Define loadPlaces function - wrapped in useEffect to prevent infinite loops
  useEffect(() => {
    loadPlacesRef.current = async (type: ExploreType, reset = false) => {
    try {
      // Synchronous guard against multiple simultaneous calls
      if (isLoadingRef.current && !reset) {
        return;
      }

      // Set synchronous loading flag immediately
      isLoadingRef.current = true;

      setState(prev => ({
        ...prev,
        loading: true,
        error: undefined
      }));

      // Access current state via ref to avoid stale closures
      const cache = stateRef.current.cache[type];
      const lastDoc = reset ? undefined : cache.lastDoc;

      // Build filter parameters
      const filters: ExploreFilters = {
        type,
        limit: pageSize,
        lastDoc
      };

      // Add active filters if they exist
      const currentFilters = activeFiltersRef.current;
      if (currentFilters) {
        // Common filters (apply to all types)
        if (currentFilters.selectedCity && currentFilters.selectedCity !== 'all' && currentFilters.selectedCity !== 'allcities') {
          filters.city = currentFilters.selectedCity;
        }
        if (currentFilters.minRating !== 'any') {
          filters.minRating = currentFilters.minRating;
        }
        if (currentFilters.selectedSubtype && currentFilters.selectedSubtype !== 'all') {
          filters.subtype = currentFilters.selectedSubtype;
        }

        // Hotel-specific filters
        if (type === 'hotel') {
          if (currentFilters.selectedTags.length > 0) {
            filters.tags = currentFilters.selectedTags;
          }
          if (currentFilters.hotelStar !== 'any') {
            filters.hotelStar = currentFilters.hotelStar;
          }
          if (currentFilters.hotelPriceRange[0] > 0 || currentFilters.hotelPriceRange[1] < 500) {
            filters.priceRange = {
              min: currentFilters.hotelPriceRange[0],
              max: currentFilters.hotelPriceRange[1]
            };
          }
        }

        // Food-specific filters
        if (type === 'food') {
          if (currentFilters.selectedCuisines.length > 0) {
            filters.selectedCuisines = currentFilters.selectedCuisines;
          }
          if (currentFilters.selectedSignatureDish.length > 0) {
            filters.selectedSignatureDish = currentFilters.selectedSignatureDish;
          }
          if (currentFilters.selectedFoodTags.length > 0) {
            filters.selectedFoodTags = currentFilters.selectedFoodTags;
          }
          if (currentFilters.foodPriceRange[0] > 0 || currentFilters.foodPriceRange[1] < 10000) {
            filters.foodPriceMin = currentFilters.foodPriceRange[0];
            filters.foodPriceMax = currentFilters.foodPriceRange[1];
          }
        }

        // Attraction-specific filters
        if (type === 'attraction') {
          if (currentFilters.bestTimeOfDay.length > 0) {
            filters.bestTimeOfDay = currentFilters.bestTimeOfDay;
          }
          if (currentFilters.bestSeason.length > 0) {
            filters.bestSeason = currentFilters.bestSeason;
          }
          if (currentFilters.difficulty !== 'any') {
            filters.difficulty = currentFilters.difficulty;
          }
          if (currentFilters.crowdLevel !== 'any') {
            filters.crowdLevel = currentFilters.crowdLevel;
          }
          if (currentFilters.typicalTimeSpent !== 'any') {
            filters.typicalTimeSpent = currentFilters.typicalTimeSpent;
          }
          if (currentFilters.selectedAttractionTags.length > 0) {
            filters.selectedAttractionTags = currentFilters.selectedAttractionTags;
          }
        }

        // Experience-specific filters
        if (type === 'experience') {
          if (currentFilters.bestTimeOfDay.length > 0) {
            filters.bestTimeOfDay = currentFilters.bestTimeOfDay;
          }
          if (currentFilters.bestSeason.length > 0) {
            filters.bestSeason = currentFilters.bestSeason;
          }
          if (currentFilters.difficulty !== 'any') {
            filters.difficulty = currentFilters.difficulty;
          }
          if (currentFilters.typicalTimeSpent !== 'any') {
            filters.typicalTimeSpent = currentFilters.typicalTimeSpent;
          }
        }
      }

      let response: { places: PlaceItem[]; hasMore: boolean; lastDoc?: string };
      
      // Check if we're in nearby mode (use refs to get current values)
      if (nearbyModeRef.current && nearbyLocationRef.current) {
        // Use nearby places API
        const typeMap: { [key: string]: string } = {
          'attraction': 'attraction',
          'food': 'food',
          'hotel': 'hotel',
          'experience': 'experience',
          'curated': 'attraction'
        };
        
        try {
          const nearbyResponse = await fetch(`${API_BASE_URL}/api/advanced-trip/nearby-places`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              latitude: nearbyLocationRef.current.latitude,
              longitude: nearbyLocationRef.current.longitude,
              maxDistance: selectedRadiusRef.current,
              types: [typeMap[type] || type],
              savedOnly: false
            }),
          });

          if (!nearbyResponse.ok) {
            throw new Error('Failed to fetch nearby places');
          }

          const nearbyData = await nearbyResponse.json();
          response = {
            places: nearbyData.places || [],
            hasMore: false,
            lastDoc: undefined
          };
        } catch (error) {
          throw error;
        }
      } else {
        // Use regular explore API
        response = await exploreService.getPlaces(filters);
      }

      setState(prev => {
        // Get the current cached places for this type
        const currentCachedPlaces = prev.cache[type].places;
        const newPlaces = reset ? response.places : [...currentCachedPlaces, ...response.places];
        
        // Deduplicate places by ID to prevent duplicate entries
        const uniquePlaces = newPlaces.reduce((acc, place) => {
          if (!acc.find(p => p.id === place.id)) {
            acc.push(place);
          }
          return acc;
        }, [] as PlaceItem[]);
        
        // Note: Markers will be updated by the separate useEffect that watches state.places and savedPlaces
        // This avoids closure issues with savedPlaces
        
        return {
          ...prev,
          currentType: type,
          places: uniquePlaces,
          loading: false,
          hasMore: response.hasMore,
          lastDoc: response.lastDoc,
          cache: {
            ...prev.cache,
            [type]: {
              places: uniquePlaces,
              lastDoc: response.lastDoc,
              hasMore: response.hasMore
            }
          }
        };
      });

      // Reset synchronous loading flag after successful completion
      isLoadingRef.current = false;

    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load places'
      }));
      
      // Reset synchronous loading flag after error
      isLoadingRef.current = false;
    }
    };
  }, [nearbyMode, nearbyLocation, selectedRadius]); // Update when nearby mode changes


  // Handle load more
  const handleLoadMore = useCallback(() => {
    const currentState = stateRef.current;
    if (!currentState.loading && currentState.hasMore) {
      loadPlacesRef.current?.(currentState.currentType, false);
    }
  }, []);

  // Handle place click (single-click on desktop - just highlight marker)
  const handlePlaceClick = useCallback((place: PlaceItem) => {
    setHighlightedPlaceId(place.id);
    setTouchedPlaceId(place.id);
  }, []);

  // Handle place double-click (desktop) - open panel
  const handlePlaceDoubleClick = useCallback((place: PlaceItem) => {
    setSelectedPlace(place);
  }, []);

  // Handle place touch (first touch on mobile)
  const handlePlaceTouch = useCallback((place: PlaceItem) => {
    // Set the touched place to show overlay
    setTouchedPlaceId(place.id);
    
    // Center map on the place and show marker
    if (place.geo_point) {
      const coords = typeof place.geo_point === 'string' 
        ? JSON.parse(place.geo_point) 
        : place.geo_point;
      
      // Create a single marker for this place
      const isSaved = savedPlaces.some(savedPlace => savedPlace.id === place.id);
      const singleMarker: MarkerData = {
        id: place.id,
        lat: coords.lat,
        lng: coords.lon,
        title: place.title,
        type: place.type as 'attraction' | 'food' | 'hotel',
        isSaved: isSaved
      };
      
      setMapMarkers([singleMarker]);
      
      // Center and zoom the map to the place
      setMapCenter({ lat: coords.lat, lng: coords.lon });
      setMapZoom(11); // Show place with wider surrounding area (district view)
    }
  }, [savedPlaces]);

  // Handle overlay click to show detail
  const handleOverlayClick = useCallback((place: PlaceItem) => {
    setSelectedPlace(place);
    setTouchedPlaceId(null); // Hide overlay
  }, []);

  // Handle place hover (desktop) - only visual highlight, no map centering
  const handlePlaceHover = useCallback((place: PlaceItem | null) => {
    setHoveredPlaceId(place?.id || null);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedPlace(null);
  }, []);

  // Filter handlers
  const handleFiltersChange = useCallback((filters: FilterState) => {
    setActiveFilters(filters);
    // CRITICAL: Update ref immediately so loadPlaces sees the new filters
    activeFiltersRef.current = filters;
    
    // Calculate active filters count
    let count = 0;
    if (filters.selectedType !== 'all') count++;
    if (filters.selectedSubtype !== 'all') count++;
    if (filters.minRating !== 'any') count++;
    if (filters.selectedCity !== 'all') count++;
    
    if (filters.selectedType === 'hotels') {
      if (filters.hotelStar !== 'any') count++;
      if (filters.selectedTags.length > 0) count++;
      if (filters.hotelPriceRange[0] > 0 || filters.hotelPriceRange[1] < 500) count++;
    }
    
    if (filters.selectedType === 'food') {
      if (filters.selectedCuisines.length > 0) count++;
      if (filters.selectedSignatureDish.length > 0) count++;
      if (filters.selectedFoodTags.length > 0) count++;
      if (filters.foodPriceRange[0] > 0 || filters.foodPriceRange[1] < 10000) count++;
    }
    
    if (filters.selectedType === 'attractions') {
      if (filters.bestTimeOfDay.length > 0) count++;
      if (filters.bestSeason.length > 0) count++;
      if (filters.difficulty !== 'any') count++;
      if (filters.crowdLevel !== 'any') count++;
      if (filters.typicalTimeSpent !== 'any') count++;
      if (filters.selectedAttractionTags.length > 0) count++;
    }
    
    if (filters.selectedType === 'experience') {
      if (filters.bestTimeOfDay.length > 0) count++;
      if (filters.bestSeason.length > 0) count++;
      if (filters.difficulty !== 'any') count++;
      if (filters.typicalTimeSpent !== 'any') count++;
    }
    
    setActiveFiltersCount(count);
    
    // Reload places with new filters
    // Map filter type values to backend ExploreType values
    let newType: ExploreType;
    if (filters.selectedType === 'all') {
      newType = 'curated'; // Use 'curated' to show all types
    } else if (filters.selectedType === 'attractions') {
      newType = 'attraction';
    } else if (filters.selectedType === 'hotels') {
      newType = 'hotel';
    } else if (filters.selectedType === 'experience') {
      newType = 'experience';
    } else {
      newType = filters.selectedType as ExploreType; // 'food' stays as 'food'
    }
    
    // Clear current places before loading filtered results
    setState(prev => ({
      ...prev,
      places: [],
      lastDoc: undefined,
      cache: {
        ...prev.cache,
        [newType]: {
          places: [],
          lastDoc: undefined,
          hasMore: true
        }
      }
    }));
    
    // Small delay to ensure state is cleared before loading
    setTimeout(() => {
      loadPlacesRef.current?.(newType, true);
    }, 0);
  }, []); // No dependencies needed

  const handleOpenFilters = useCallback(() => {
    setShowFilters(true);
  }, []);

  const handleCloseFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  // Perform actual nearby search with selected radius
  const performNearbySearch = useCallback(async (radius: number) => {
    setIsSearchingNearby(true);
    try {
      // Request user's current location
      if (!navigator.geolocation) {
        showToast('Geolocation is not supported by your browser', 'error');
        setIsSearchingNearby(false);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Store the location and radius - UPDATE REFS FIRST so loadPlaces sees current values
            nearbyLocationRef.current = { latitude, longitude };
            selectedRadiusRef.current = radius;
            nearbyModeRef.current = true;
            
            // Then update state
            setNearbyLocation({ latitude, longitude });
            setSelectedRadius(radius);
            
            // Set map center to user's location
            setMapCenter({ lat: latitude, lng: longitude });
            setMapZoom(12);
            
            showToast(`Location detected! Loading places within ${radius}km...`, 'success');
            
            // Activate nearby mode (this will trigger useEffect to load places)
            setNearbyMode(true);
            
            setIsSearchingNearby(false);
          } catch (error) {
            showToast('Error processing your location', 'error');
            setIsSearchingNearby(false);
          }
        },
        (error) => {
          let message = 'Unable to access your location';
          if (error.code === error.PERMISSION_DENIED) {
            message = 'Location permission denied. Please enable location access.';
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            message = 'Your location is currently unavailable';
          } else if (error.code === error.TIMEOUT) {
            message = 'Location request timed out';
          }
          showToast(message, 'error');
          setIsSearchingNearby(false);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } catch (error) {
      showToast('Error searching nearby places', 'error');
      setIsSearchingNearby(false);
    }
  }, [showToast]);

  // Toggle nearby mode or show radius dropdown
  const handleSearchNearby = useCallback(() => {
    if (nearbyMode) {
      // Exit nearby mode - restore default places
      setNearbyMode(false);
      setNearbyLocation(null);
      setShowRadiusDropdown(false);
      showToast('Exited nearby mode', 'info');
      
      // Reload default places for current type
      const currentType = stateRef.current.currentType;
      setState(prev => ({
        ...prev,
        places: [],
        lastDoc: undefined,
        cache: {
          ...prev.cache,
          [currentType]: {
            places: [],
            lastDoc: undefined,
            hasMore: true
          }
        }
      }));
      
      // Small delay to ensure state is cleared before loading
      setTimeout(() => {
        loadPlacesRef.current?.(currentType, true);
      }, 0);
    } else {
      // Show radius dropdown to select distance
      setShowRadiusDropdown(prev => !prev);
    }
  }, [nearbyMode, showToast]);

  // Update markers when savedPlaces changes
  useEffect(() => {
    let updatedMarkers: MarkerData[] = [];
    
    // Add current location marker if nearby mode is active
    if (nearbyMode && nearbyLocation) {
      updatedMarkers.push({
        id: 'user-location',
        lat: nearbyLocation.latitude,
        lng: nearbyLocation.longitude,
        title: 'Your Location',
        type: 'attraction', // Using 'attraction' as a placeholder type
        isSaved: false
      } as MarkerData);
    }
    
    // Add place markers
    if (state.places.length > 0) {
      const placeMarkers = state.places
        .map(place => {
          // Parse geo_point if it's a JSON string
          let geoPoint = place.geo_point;
          if (typeof geoPoint === 'string') {
            try {
              geoPoint = JSON.parse(geoPoint);
            } catch (e) {
              return null;
            }
          }
          
          // Check if we have valid coordinates
          if (geoPoint && geoPoint.lat && geoPoint.lon) {
            // Check if this place is saved
            const isSaved = savedPlaces.some(savedPlace => savedPlace.id === place.id);
            
            return {
              id: place.id,
              lat: geoPoint.lat,
              lng: geoPoint.lon,
              title: place.title,
              type: place.type as 'attraction' | 'food' | 'hotel' | 'experience',
              isSaved: isSaved
            } as MarkerData;
          }
          return null;
        })
        .filter((marker): marker is MarkerData => marker !== null);
      
      updatedMarkers = [...updatedMarkers, ...placeMarkers];
    }
    
    setMapMarkers(updatedMarkers);
  }, [savedPlaces, state.places, nearbyMode, nearbyLocation]);

  // Load initial data
  useEffect(() => {
    loadPlacesRef.current?.('attraction', true);
  }, []); // Empty array - only runs once


  return (
    <div className="h-full w-full flex flex-col bg-gray-50 overflow-hidden">
      {/* Desktop Layout */}
      <div className="hidden md:flex h-full w-full">
        {/* Left Side - Explore Content */}
        <div className="w-1/2 flex flex-col border-r border-gray-200 overflow-hidden h-full">
          {/* Header - Fixed */}
          <div className="bg-white border-b border-gray-200 px-4 py-4 flex-shrink-0">
            <h1 className="text-xl font-bold text-gray-800 mb-4">Explore Sri Lanka</h1>
            
            {/* Filter and Search Buttons - Desktop */}
            <div className="flex gap-2 items-center justify-end">
              <div className="relative inline-block">
                <button
                  onClick={handleSearchNearby}
                  disabled={isSearchingNearby}
                  className={`flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors touch-manipulation ${
                    nearbyMode 
                      ? 'bg-[#663399] text-white border-2 border-[#663399] hover:bg-[#552288]' 
                      : 'bg-white border-2 border-[#663399] text-[#663399] hover:bg-[#F8F6FB] active:bg-[#E0D7FA]'
                  }`}
                  title={nearbyMode ? `Exit nearby mode (${selectedRadius}km)` : "Search places near your location"}
                >
                  {isSearchingNearby ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <MapPin size={16} />
                  )}
                  <span>{isSearchingNearby ? 'Locating...' : nearbyMode ? `${selectedRadius}km` : 'Nearby'}</span>
                </button>
                <RadiusDropdown
                  isOpen={showRadiusDropdown}
                  onSelectRadius={performNearbySearch}
                  onClose={() => setShowRadiusDropdown(false)}
                />
              </div>
              <FilterButton
                onOpenFilters={handleOpenFilters}
                activeFiltersCount={activeFiltersCount}
              />
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto min-h-0 bg-white">
            {state.error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-6xl mb-4">⚠️</div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Places</h3>
                  <p className="text-gray-600 mb-4">{state.error}</p>
                  <button
                    onClick={() => loadPlacesRef.current?.(state.currentType, true)}
                    className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : (
              <ExploreGrid
                items={state.places}
                loading={state.loading}
                hasMore={state.hasMore}
                onLoadMore={handleLoadMore}
                onClickItem={handlePlaceClick}
                onDoubleClickItem={handlePlaceDoubleClick}
                onHoverItem={handlePlaceHover}
                onTouchItem={handlePlaceTouch}
                touchedPlaceId={touchedPlaceId}
                onOverlayClick={handleOverlayClick}
                variant="type"
                pageSize={pageSize}
              />
            )}
          </div>

          {/* Footer Info - Fixed */}
          <div className="bg-white border-t border-gray-200 px-4 py-2 flex-shrink-0">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>
                Showing {state.places.length} places
                {state.hasMore && ' • Scroll for more'}
              </span>
              <span>
                {`${state.currentType.charAt(0).toUpperCase() + state.currentType.slice(1)}s in Sri Lanka`}
              </span>
            </div>
          </div>
        </div>

        {/* Right Side - Map */}
        <div className="w-1/2 relative overflow-hidden flex-shrink-0">
          <MapViewLite
            markers={mapMarkers}
            highlightedMarkerId={highlightedPlaceId}
            hoveredMarkerId={hoveredPlaceId}
            center={mapCenter}
            zoom={mapZoom}
            loading={state.loading}
            interactive={true}
            className="h-full w-full"
            onMarkerClick={(markerId) => {
              // Find the place and open detail panel
              const place = state.places.find(p => p.id === markerId);
              if (place) {
                setHighlightedPlaceId(place.id);
                setTouchedPlaceId(place.id);
                setSelectedPlace(place); // Open detail panel
              }
            }}
          />
        </div>
      </div>

      {/* Mobile Layout */}
      <div 
        className="md:hidden h-full flex flex-col relative"
        style={{ touchAction: 'pan-y' }}
      >
        {/* Fixed Map Section */}
        <div 
          className="border-b border-gray-200 relative z-10"
          style={{ 
            height: `calc(${mobileMapHeight}vh - 60px)`,
            transition: isDragging ? 'none' : 'height 0.3s ease-out',
          }}
        >
            <MapViewLite
              markers={mapMarkers}
              highlightedMarkerId={highlightedPlaceId}
              center={mapCenter}
              zoom={mapZoom}
              loading={state.loading}
              interactive={true}
              className="h-full"
              onMarkerClick={(markerId) => {
              // Find the place and open detail panel
              const place = state.places.find(p => p.id === markerId);
              if (place) {
                setHighlightedPlaceId(place.id);
                setTouchedPlaceId(place.id);
                setSelectedPlace(place); // Open detail panel
              }
            }}
          />
        </div>

        {/* Swipeable Explore Section - Bottom Sheet */}
        <div 
          className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-lg z-20 flex flex-col"
          style={{ 
            height: `${sheetHeight}vh`,
            maxHeight: `calc(100vh - 60px)`, // Account for bottom nav (60px)
            transition: isDragging ? 'none' : 'height 0.3s ease-out',
          }}
        >
          {/* Drag handle area */}
          <div
            className="w-full py-3 flex flex-col items-center cursor-grab active:cursor-grabbing touch-none"
            {...dragHandlers}
            onClick={() => {
              if (sheetState === 'collapsed') {
                expand();
              }
            }}
          >
            <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            
            {/* Header - visible only when collapsed */}
            {sheetState === 'collapsed' && (
              <div className="text-sm font-semibold text-gray-900 mt-2">
                Show results
              </div>
            )}
          </div>

          {/* Explore Content - hidden when collapsed */}
          {sheetState !== 'collapsed' && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Header - also draggable */}
              <div 
                className="bg-white border-b border-gray-200 px-3 py-1 cursor-grab active:cursor-grabbing touch-none"
                {...dragHandlers}
              >
                <div className="flex items-center justify-between gap-2">
                  <h1 className="text-base font-bold text-gray-800 flex-1 min-w-0">Explore Sri Lanka</h1>
                  
                  {/* Search Nearby and Filter Buttons - Mobile */}
                  <div 
                    onClick={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                    className="touch-none flex gap-2 flex-shrink-0"
                  >
                    <div className="relative inline-block">
                      <button
                        onClick={handleSearchNearby}
                        disabled={isSearchingNearby}
                        className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                          nearbyMode 
                            ? 'bg-[#663399] text-white border-2 border-[#663399] hover:bg-[#552288]' 
                            : 'bg-white border-2 border-[#663399] text-[#663399] hover:bg-[#F8F6FB] active:bg-[#E0D7FA]'
                        }`}
                        title={nearbyMode ? `Exit nearby mode (${selectedRadius}km)` : "Search places near your location"}
                      >
                        {isSearchingNearby ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <MapPin size={16} />
                        )}
                        <span className="hidden xs:inline">
                          {isSearchingNearby ? 'Locating...' : nearbyMode ? `${selectedRadius}km` : 'Nearby'}
                        </span>
                      </button>
                      <RadiusDropdown
                        isOpen={showRadiusDropdown}
                        onSelectRadius={performNearbySearch}
                        onClose={() => setShowRadiusDropdown(false)}
                      />
                    </div>
                    <FilterButton
                      onOpenFilters={handleOpenFilters}
                      activeFiltersCount={activeFiltersCount}
                      className="flex-shrink-0"
                    />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden">
                {state.error ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="text-6xl mb-4">⚠️</div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Places</h3>
                      <p className="text-gray-600 mb-4">{state.error}</p>
                      <button
                        onClick={() => loadPlacesRef.current?.(state.currentType, true)}
                        className="bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                ) : (
                  <div ref={contentScrollRef} className="h-full overflow-y-auto bg-white overscroll-y-contain">
                    <ExploreGrid
                      items={state.places}
                      loading={state.loading}
                      hasMore={state.hasMore}
                      onLoadMore={handleLoadMore}
                      onClickItem={handlePlaceClick}
                      onDoubleClickItem={handlePlaceDoubleClick}
                      onHoverItem={handlePlaceHover}
                      onTouchItem={handlePlaceTouch}
                      touchedPlaceId={touchedPlaceId}
                      onOverlayClick={handleOverlayClick}
                      variant="type"
                      pageSize={pageSize}
                    />
                  </div>
                )}
              </div>

              {/* Footer Info - Hidden on mobile */}
              <div className="hidden md:block bg-white border-t border-gray-200 px-4 pt-2 pb-1">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>
                    Showing {state.places.length} places
                    {state.hasMore && ' • Scroll for more'}
                  </span>
                  <span>
                    {`${state.currentType.charAt(0).toUpperCase() + state.currentType.slice(1)}s in Sri Lanka`}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Place Detail Modal */}
      {selectedPlace && (
        <PlaceDetailPanel
          place={selectedPlace}
          onClose={handleCloseDetail}
        />
      )}

      {/* Filter Modal */}
      <TravelFilterUI
        onFiltersChange={handleFiltersChange}
        onClose={handleCloseFilters}
        isOpen={showFilters}
      />
    </div>
  );
};

export default Explore;
